import { friendlyMessage } from '../../../../components/ui/friendlyMessage';
import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';

import CourseModule from './CourseModule'
import VideoPlayer from './VideoPlayer'
import CourseDocument from './CourseDocument'
import CourseQuiz from './CourseQuiz'
import CourseSurvey from './CourseSurvey'
import NoData from '../../../../components/common/NoData';
import Loader from '../../../../components/common/Loader'
import ContentUnavailable from '../../../../components/common/ContentUnavailable'
import { decodeData } from '../../../../utils/encodeAndEncode';
import { getModuleList, verifyUserCourseAccess } from '../../../../services/user/course';
import { isNotFoundError } from '../../../../utils/isNotFoundError';
import { getSignedMediaUrl } from '../../../../services/media.service';
import { useUserCourse } from '../../../../contexts/UserCourseContext';

const isPlayableDocumentUrl = (value) => {
  if (!value || typeof value !== 'string') return false;
  const url = value.trim();
  if (!/^https?:\/\//i.test(url)) return false;
  if (url.includes('/api/media/')) return true;
  if (url.includes('.amazonaws.com/') || url.includes('X-Amz-')) return true;
  if (url.includes('nexgenversity/')) return false;
  return true;
};

function CourseWatch() {
  const { setCourseName } = useUserCourse();
  const [moduleList, setModuleList] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [courseStatus, setCourseStatus] = useState('loading');
  const [unavailableMessage, setUnavailableMessage] = useState('');
  const [error, setError] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mentorId, setMentorId] = useState(null);
  const [resolvedContentUrl, setResolvedContentUrl] = useState(null);
  const [isContentUrlLoading, setIsContentUrlLoading] = useState(false);

  const { encodedId } = useParams();
  const decoded = decodeData(encodedId);
  const courseId = decoded?.id;

  useEffect(() => {
    setCourseName('');
    setSelectedContent(null);
    setModuleList([]);
    setResolvedContentUrl(null);
    if (!courseId) {
      setCourseStatus('unavailable');
      setUnavailableMessage('Invalid course ID');
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const verifyAndLoad = async () => {
      setCourseStatus('loading');
      setIsLoading(true);
      try {
        const response = await verifyUserCourseAccess({
          course_id: courseId,
          mode: 'watch',
        });
        if (cancelled) return;
        if (response?.success) {
          setCourseStatus('available');
          await fetchModuleList();
          return;
        }
        setCourseStatus('unavailable');
        setUnavailableMessage('This course is not available');
      } catch (verifyError) {
        if (cancelled) return;
        const message = verifyError?.message || '';
        setUnavailableMessage(
          message.includes('access')
            ? 'You no longer have access to this course'
            : 'This course is not available'
        );
        setCourseStatus('unavailable');
        setIsLoading(false);
      }
    };

    verifyAndLoad();

    return () => {
      cancelled = true;
    };
  }, [courseId, setCourseName]);

  useEffect(() => {
    if (moduleList.length > 0 && !selectedContent) {
      const savedId = courseId
        ? localStorage.getItem(`course-selected-content-${courseId}`)
        : null;

      let contentToSelect = null;
      if (savedId != null) {
        for (const module of moduleList) {
          const match = module.content?.find(
            (item) => String(item.id) === String(savedId)
          );
          if (match) {
            contentToSelect = match;
            break;
          }
        }
      }

      if (!contentToSelect) {
        contentToSelect = moduleList
          .flatMap((module) => module.content || [])
          .find(Boolean) || null;
      }

      if (contentToSelect) {
        setIsContentUrlLoading(true);
        setSelectedContent(contentToSelect);
      }
    }
  }, [moduleList, courseId]);


  // Handle body overflow when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [showMobileMenu]);

  useEffect(() => {
    if (!showMobileMenu) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowMobileMenu(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [showMobileMenu]);

  async function fetchModuleList() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getModuleList({ course_id: courseId });

      if (response?.data?.response) {
        setCourseName(response.data.course_name || '');
        const mentorIdFromResponse = response.data.mentor_id;
        if (mentorIdFromResponse) {
          setMentorId(mentorIdFromResponse);
        }

        const sortedModules = response.data.response.map(module => ({
          ...module,
          content: Array.isArray(module.content)
            ? [...module.content]
                .map((item) => {
                  const completed = typeof item.completed === 'number' ? item.completed : 0;
                  if (item.type === 'video') {
                    return {
                      ...item,
                      completed,
                      upload_view_type: item.upload_view_type || 'uploaded',
                      video_url: item.video_url || null,
                    };
                  }
                  return { ...item, completed };
                })
                .sort((a, b) => (a.serial_no || 0) - (b.serial_no || 0))
            : []
        }));

        // console.log('[WatchCourse] All Modules & Content:', sortedModules);

        setModuleList(sortedModules);
      } else {
        setError('No course content available');
      }
    } catch (error) {
      console.error('[WatchCourse] getModuleList error:', error);
      if (isNotFoundError(error)) {
        setCourseStatus('unavailable');
        setUnavailableMessage('This course is not available');
      } else {
        setError('No course content available');
      }
      setModuleList([]);
    } finally {
      setIsLoading(false);
    }
  }

  const handleContentClick = (content) => {
    if (!content) return;
    if (String(selectedContent?.id) === String(content.id) && selectedContent?.type === content.type) {
      setShowMobileMenu(false);
      return;
    }
    setResolvedContentUrl(null);
    setIsContentUrlLoading(true);
    setSelectedContent(content);
    if (courseId && content.id != null) {
      localStorage.setItem(`course-selected-content-${courseId}`, String(content.id));
    }
    setShowMobileMenu(false);
  };

  useEffect(() => {
    let active = true;

    const resolveContentUrl = async () => {
      try {
        if (!selectedContent) {
          setResolvedContentUrl(null);
          setIsContentUrlLoading(false);
          return;
        }

        setIsContentUrlLoading(true);
        setResolvedContentUrl(null);

        if (
          selectedContent.type === 'video' &&
          (selectedContent.upload_view_type === 'youtube' || selectedContent.upload_view_type === 'src')
        ) {
          if (active) setResolvedContentUrl(selectedContent.video_url || selectedContent.url || null);
          return;
        }

        if (selectedContent.type === 'video' && selectedContent.media_id) {
          const url = await getSignedMediaUrl({
            type: 'video',
            mediaId: selectedContent.media_id,
          });
          if (active) setResolvedContentUrl(url || null);
          return;
        }

        if (selectedContent.type === 'document') {
          if (isPlayableDocumentUrl(selectedContent.url)) {
            if (active) setResolvedContentUrl(selectedContent.url);
            return;
          }
          if (selectedContent.media_id) {
            const url = await getSignedMediaUrl({
              type: 'document',
              mediaId: selectedContent.media_id,
            });
            if (active) setResolvedContentUrl(url || null);
            return;
          }
          if (active) setResolvedContentUrl(null);
          return;
        }

        setResolvedContentUrl(selectedContent.url || null);
      } catch (error) {
        if (!active) return;
        if (selectedContent?.type === 'document') {
          setResolvedContentUrl(
            isPlayableDocumentUrl(selectedContent.url) ? selectedContent.url : null
          );
          return;
        }
        setResolvedContentUrl(selectedContent?.url || null);
      } finally {
        if (active) setIsContentUrlLoading(false);
      }
    };

    resolveContentUrl();
    return () => {
      active = false;
    };
  }, [selectedContent]);

  const handleVideoProgress = (videoId, fullyWatched) => {
    setModuleList(prevModules => {
      const updatedModules = prevModules.map(module => ({
        ...module,
        content: module.content.map(item =>
          String(item.id) === String(videoId)
            ? { ...item, completed: 1, is_fully_watched: fullyWatched ? 1 : item.is_fully_watched }
            : item
        )
      }));
      return updatedModules;
    });
  };

  const handleAssessmentCompletion = async (assessmentId, isPassed) => {
    if (isPassed) {
      setModuleList(prevModules => {
        return prevModules.map(module => ({
          ...module,
          content: module.content.map(item =>
            String(item.id) === String(assessmentId) ? { ...item, completed: 1 } : item
          )
        }));
      });
    }
  };

  const handleSurveyCompletion = async (surveyId) => {
    setModuleList(prevModules => {
      return prevModules.map(module => ({
        ...module,
        content: module.content.map(item =>
          String(item.id) === String(surveyId) ? { ...item, completed: 1 } : item
        )
      }));
    });
  };

  const handleDocumentViewed = useCallback((documentId) => {
    setModuleList((prevModules) =>
      prevModules.map((module) => ({
        ...module,
        content: module.content.map((item) =>
          String(item.id) === String(documentId) && item.type === 'document'
            ? { ...item, completed: 1 }
            : item
        ),
      }))
    );
  }, []);

  const LoadingSpinner = () => (
    <Loader variant="page" caption="Loading course content..." />
  );

  const ErrorMessage = ({ message }) => (
    <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '60vh' }}>
      <NoData message={message} />
    </div>
  );

  const renderContent = () => {

    if (!selectedContent) {
      return (
        <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '60vh' }}>
          <NoData caption="No content available in this course." />
        </div>
      );
    }

    switch (selectedContent.type?.toLowerCase()) {
      case 'video':
        if (isContentUrlLoading) return <Loader variant="page" caption="Loading video..." />;
        if (!resolvedContentUrl) return <NoData message="This video is unavailable right now." />;
        return (
          <>
            <VideoPlayer
              videoUrl={resolvedContentUrl}
              contentData={selectedContent}
              courseId={courseId}
              mentorId={mentorId}
              onVideoProgress={handleVideoProgress}
            />
          </>
        );
      case 'document':
        if (isContentUrlLoading) return <Loader variant="page" caption="Loading document..." />;
        return (
          <CourseDocument
            documentUrl={resolvedContentUrl}
            documentId={selectedContent?.id}
            courseId={courseId}
            onDocumentViewed={handleDocumentViewed}
          />
        );
      case 'assessment':
        return <CourseQuiz
          assessmentId={selectedContent.id}
          moduleData={selectedContent}
          onAssessmentComplete={handleAssessmentCompletion}
        />;
      case 'survey':
        return <CourseSurvey
          surveyId={selectedContent.id}
          moduleData={selectedContent}
          onSurveyComplete={handleSurveyCompletion}
        />;
      default:
        return (
          <div className="tw-text-center tw-p-12">
            <p>Unsupported content type: {selectedContent.type}</p>
          </div>
        );
    }
  };

  if (courseStatus === 'loading' || isLoading) return <LoadingSpinner />;

  if (courseStatus === 'unavailable') {
    return (
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <ContentUnavailable
            title={unavailableMessage || 'This course is not available'}
            description="It may have been removed, deleted, or you no longer have access to it."
            icon="mdi:book-remove-outline"
            backLabel="Back to Courses"
            backTo="/user/courses"
          />
        </div>
      </div>
    );
  }

  if (error) return <ErrorMessage message={friendlyMessage(error)} />;

  return (
    <div className="course-watch-container tw-w-full tw-min-w-0 tw-bg-white tw-p-3 sm:tw-p-4">
      <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between min-[1024px]:tw-hidden">
        <span className="tw-text-base tw-font-semibold tw-text-slate-800">Course lesson</span>
        <button
          type="button"
          className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700"
          onClick={() => setShowMobileMenu(true)}
          aria-label="Open course contents"
          aria-expanded={showMobileMenu}
          aria-controls="course-content-sidebar"
        >
          <Icon icon="mdi:format-list-bulleted" width="20" height="20" /> Contents
        </button>
      </div>

      <div className="tw-grid tw-min-w-0 tw-grid-cols-1 tw-gap-5 min-[1024px]:tw-grid-cols-[minmax(0,1fr)_minmax(280px,320px)]">
        <main className="tw-min-w-0">{renderContent()}</main>
        {showMobileMenu && (
          <button
            type="button"
            className="tw-fixed tw-inset-0 tw-z-[9998] tw-border-0 tw-bg-slate-900/50 min-[1024px]:tw-hidden"
            aria-label="Close course contents"
            onClick={() => setShowMobileMenu(false)}
          />
        )}
        <aside
          id="course-content-sidebar"
          className={`course-module-wrapper tw-min-w-0 max-[1023px]:tw-fixed max-[1023px]:tw-inset-y-0 max-[1023px]:tw-right-0 max-[1023px]:tw-z-[9999] max-[1023px]:tw-w-[min(100vw,380px)] max-[1023px]:tw-overflow-y-auto max-[1023px]:tw-bg-white max-[1023px]:tw-p-4 max-[1023px]:tw-shadow-xl max-[1023px]:tw-transition-transform min-[1024px]:tw-self-start ${showMobileMenu ? 'max-[1023px]:tw-visible max-[1023px]:tw-translate-x-0' : 'max-[1023px]:tw-invisible max-[1023px]:tw-translate-x-full'}`}
        >
          <button
            type="button"
            className="tw-ml-auto tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white min-[1024px]:tw-hidden"
            onClick={() => setShowMobileMenu(false)}
            aria-label="Close course contents"
          >
            <Icon icon="mdi:close" width="22" height="22" />
          </button>
          <CourseModule
            courseModules={moduleList}
            courseId={courseId}
            onContentClick={handleContentClick}
            selectedContent={selectedContent}
          />
        </aside>
      </div>
    </div>
  );
}

export default CourseWatch;
